// Соответствие «экран -> статья и раздел справки»: единственное место, где
// живут цели кнопок «?». Больше нигде в приложении якорей нет.
//
// Цель записывается как `<файл статьи без .md>#<якорь раздела>`. Якорь считается
// из заголовка по правилу GitHub (см. slugify в markdown.ts), поэтому читается
// глазами и совпадает с якорем в markdown в репозитории.
//
// Переименовали заголовок в статье — цель молча перестанет попадать в раздел.
// Чтобы это не ушло в прод незамеченным, тест help.test.ts проверяет, что для
// каждой цели есть статья и в ней есть такой якорь.

import { matchPath } from 'react-router-dom';

/** Цель: статья и, необязательно, раздел внутри неё. */
export interface HelpTarget {
  slug: string;
  anchor?: string;
}

/**
 * Статья и раздел по маршруту экрана. Кнопка «?» берёт цель отсюда по текущему
 * адресу, поэтому экраны о якорях ничего не знают.
 *
 * Экрана нет в списке — панель открывается на оглавлении.
 */
export const HELP_BY_ROUTE: Record<string, string> = {
  // до входа: вход, регистрация и активация — одна статья, разные разделы.
  // Справка там работает так же, как внутри системы: HelpProvider обёрнут и
  // вокруг публичных страниц (App.tsx), а /help открыт без авторизации.
  '/login': '01-vhod-i-roli#как-войти',
  '/register': '01-vhod-i-roli#как-зарегистрироваться',
  '/activate': '01-vhod-i-roli#как-зарегистрироваться',
  '/not-activated': '01-vhod-i-roli#как-зарегистрироваться',
  // карточка ЦБ с графиком выплат открывается модальным окном со списка
  '/dict/securities': '02-karta-cb-i-grafik#как-заполнить-график-выплат',
  '/income-payout/schedule': '05-monitoring#график',
  // карточка строки графика: номер строки в адресе, ключ с параметром
  '/income-payout/schedule/:id': '03-vyplata-dohoda#карточка-строки-графика',
  '/income-payout/receipts': '03-vyplata-dohoda#шаг-1-поступление-дохода',
  '/income-payout/accruals': '03-vyplata-dohoda#шаг-2-начисление-дохода',
  '/income-payout/payouts': '03-vyplata-dohoda#шаг-3-выплата-дохода',
  '/income-payout/holds': '04-rekvizity-i-priostanovka#приостановка-выплат-депоненту',
  // Журнал начислений по тарифам: вкладка «Выгрузки в 1С» у статьи своя
  '/accruals': '14-vygruzka-nachisleniy-v-1s#как-выгрузить',
  '/documents/income-requisites': '04-rekvizity-i-priostanovka#реквизиты-по-доходам',
  '/admin/users': '09-ssylka-aktivacii#как-выслать',
  '/password': '11-smena-parolya#как-сменить',
  '/forgot-password': '11-smena-parolya#если-пароль-забыт',
  '/reset-password': '11-smena-parolya#если-пароль-забыт',
  // Экраны контрагентов разложены по типам, но карточка у них одна
  '/dict/authorized-depositaries': '12-spravochnik-kontragentov#как-заполнить-карточку',
  '/dict/owners': '12-spravochnik-kontragentov#как-заполнить-карточку',
  '/dict/issuers': '12-spravochnik-kontragentov#как-заполнить-карточку',
  '/dict/registrars': '12-spravochnik-kontragentov#как-заполнить-карточку',
  '/dict/depositaries': '12-spravochnik-kontragentov#как-заполнить-карточку',
  '/dict/exchanges': '12-spravochnik-kontragentov#как-заполнить-карточку',
};

/**
 * Вкладки страницы «График выплат»: один маршрут, три разных раздела статьи.
 * Значение по маршруту (`HELP_BY_ROUTE`) соответствует вкладке «График».
 *
 * Страница передаёт отсюда готовую цель, а не собирает её сама: якоря остаются
 * в одном файле.
 */
export const SCHEDULE_TAB_HELP: Record<string, string> = {
  schedule: '05-monitoring#график',
  monitor: '05-monitoring#мониторинг',
  issues: '05-monitoring#ошибки-ввода',
};

/** Разобрать цель на статью и якорь. */
export function parseTarget(target: string): HelpTarget {
  const [slug, anchor] = target.split('#');
  return anchor ? { slug, anchor } : { slug };
}

/**
 * Подходит ли ключ к адресу. Обычный ключ — сам адрес или его начало по
 * границе сегмента. Ключ с параметром (`/income-payout/schedule/:id`) — как
 * маршрут react-router: так же сравнивает адрес и гард маршрутов, и номер в
 * адресе не путается с вложенным экраном.
 */
function keyMatches(key: string, pathname: string): boolean {
  if (key.includes('/:')) return matchPath({ path: key, end: false }, pathname) !== null;
  return pathname === key || pathname.startsWith(`${key}/`);
}

/**
 * Цель для адреса экрана. Берётся самое длинное подошедшее совпадение, чтобы
 * вложенные адреса достались своему экрану; `undefined` — записи нет.
 * Ключ с параметром длиннее ключа своего раздела, поэтому карточка строки
 * получает свою статью, а не статью графика.
 */
export function helpForRoute(pathname: string): string | undefined {
  let bestKey = '';
  for (const key of Object.keys(HELP_BY_ROUTE)) {
    if (!keyMatches(key, pathname)) continue;
    if (key.length > bestKey.length) bestKey = key;
  }
  return bestKey ? HELP_BY_ROUTE[bestKey] : undefined;
}

/**
 * Что показать в панели: явная цель, иначе цель по маршруту, иначе `null` —
 * оглавление (для экранов, у которых своей статьи нет).
 *
 * Отдельной функцией, а не внутри компонента: это всё решение «что откроется»,
 * и его надо проверять без DOM.
 */
export function resolveHelpTarget(
  explicit: string | undefined,
  pathname: string,
): string | null {
  return explicit ?? helpForRoute(pathname) ?? null;
}

/** Адрес полной страницы справки по цели. */
export function helpUrl(target: string): string {
  return `/help/${target}`;
}
